import { alpha, Box, Divider, ListItemButton, ListItemText, useMediaQuery, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import React from 'react';
import { useSelector } from 'store';
import { ChatUserModel } from 'types/chat-users/chat-user';
import UserAvatar from './ChatUserAvatar';
import UserAssignee from './ChatUserAssignee';
import { ChatUsersCustomizationCustomColorOptions } from 'types/company-platforms/customizationSettings';
import UserActions from './ChatUserActions';
import UserDateAndUnread from './ChatUserDateAndUnread';
import { NavLink } from 'react-router';
import UserNameAndLabel from './ChatUserNameAndLabel';
import UserMessagePreview from './ChatUserMessagePreview';
import { DeleteUsersConfirmation } from 'types/chat-users/chat-users-list';
import { ChatViewId, getCurrentViewPathById, useCurrentChatView } from 'hooks/useCurrentChatView';
import MarketingConsentIndicator from './MarketingConsentIndicator';

interface SingleChatUserComponentProps {
  user: ChatUserModel;
  selected?: boolean;
  onChatUserClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, user: ChatUserModel) => void;
  interactive?: boolean;
  handleDeleteUsersConfirmation?: (params: Partial<DeleteUsersConfirmation>) => void;
  viewId?: ChatViewId;
}

const SingleChatUser: React.FC<SingleChatUserComponentProps> = React.memo(
  ({
    user,
    selected,
    onChatUserClick = () => {},
    interactive = true,
    handleDeleteUsersConfirmation = () => {},
    viewId,
  }) => {
    const theme = useTheme();
    const matchesBigMobile = useMediaQuery(theme.breakpoints.down(768));
    const currentView = useCurrentChatView();

    const { chatUsersCustomizationSettings, chatUsersCustomizationAdditionalSettings } = useSelector(
      (state) => state.companyPlatforms,
    );

    const { defaultStyles, hoverStyles, selectedStyles, unreadStyles, contactsMargin } = React.useMemo(() => {
      const defaultStyles =
        chatUsersCustomizationSettings?.find((option) => option.type === 'default') || null;
      const hoverStyles = chatUsersCustomizationSettings?.find((option) => option.type === 'hover') || null;
      const selectedStyles =
        chatUsersCustomizationSettings?.find((option) => option.type === 'selected') || null;
      const unreadStyles = chatUsersCustomizationSettings?.find((option) => option.type === 'unread') || null;
      const contactsMargin = chatUsersCustomizationAdditionalSettings?.contactsMargin ?? false;

      return { defaultStyles, hoverStyles, selectedStyles, unreadStyles, contactsMargin };
    }, [chatUsersCustomizationAdditionalSettings?.contactsMargin, chatUsersCustomizationSettings]);

    const [isHovered, setIsHovered] = React.useState(false);

    const getElementColor = (type: ChatUsersCustomizationCustomColorOptions) => {
      const unreadMessagesNumber = user.UnreadMessagesNumber;

      const styles = selected
        ? selectedStyles
        : isHovered
          ? hoverStyles
          : unreadMessagesNumber && unreadMessagesNumber > 0
            ? unreadStyles
            : defaultStyles;

      return styles?.value === 'custom' ? styles.customColor[type] + ' !important' : null;
    };

    //* Assignee color
    const assigneColor = getElementColor('assignee');

    return (
      <React.Fragment>
        <ListItemButton
          sx={{
            '&:not(.Mui-selected):hover': {
              bgcolor:
                hoverStyles?.value === 'custom'
                  ? `${hoverStyles.customColor?.background} !important`
                  : // : '#F4F4F4 !important',
                    undefined,
            },
            bgcolor: selected
              ? selectedStyles?.value === 'custom'
                ? `${selectedStyles.customColor?.background} !important`
                : // : '#E5E5E5 !important'
                  undefined
              : (user.UnreadMessagesNumber || 0) > 0
                ? unreadStyles?.value === 'custom'
                  ? `${unreadStyles.customColor?.background} !important`
                  : // : '#F1F7FC'
                    alpha(theme.palette.info.main, 0.1)
                : defaultStyles?.value === 'custom'
                  ? `${defaultStyles.customColor?.background} !important`
                  : // : 'common.white',
                    undefined,
            p: 1.5,
            mb: contactsMargin ? 1 : 0,
            boxShadow: contactsMargin ? '0px 4px 10px 0px rgba(0, 0, 0, 0.12)' : undefined,
            borderRadius: contactsMargin ? 1 : undefined,
            border: contactsMargin ? '1px solid #E6EBEF' : undefined,
            height: 84,
          }}
          onClick={(e) => {
            onChatUserClick(e, user);
          }}
          selected={selected}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          component={NavLink}
          to={
            (viewId 
              ? getCurrentViewPathById(viewId) 
              : currentView.path
            ) + '/' + user.UserDisplayPhoneNumber
          }
        >
          <UserAvatar user={user} />
          <ListItemText
            sx={{
              ml: 0.5,
            }}
          >
            <Grid container columnSpacing={1} rowSpacing={0} flexWrap="nowrap">
              <Grid size="grow" overflow={'hidden'}>
                <Box sx={{ mb: 1, mt: 0.25, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <UserNameAndLabel
                    isRestricted24={user.isRestricted24 ?? false}
                    labelJson={user.LableName}
                    fullName={user.FullName ?? ''}
                    userDisplayPhoneNumber={user.UserDisplayPhoneNumber ?? ''}
                    platform={user.Platform ?? ''}
                    unreadMessagesNumber={user.UnreadMessagesNumber ?? 0}
                    selected={selected}
                    hoverStyles={hoverStyles}
                    selectedStyles={selectedStyles}
                    unreadStyles={unreadStyles}
                    defaultStyles={defaultStyles}
                  />
                  <MarketingConsentIndicator level={user.marketingConsentLevel} />
                </Box>
                <Box>
                  <UserMessagePreview
                    user={user}
                    selected={selected}
                    hoverStyles={hoverStyles}
                    selectedStyles={selectedStyles}
                    unreadStyles={unreadStyles}
                    defaultStyles={defaultStyles}
                  />
                </Box>
              </Grid>
              <Grid size="auto">
                <Box
                  sx={{
                    width: 120,
                    [theme.breakpoints.down(480)]: {
                      width: 100,
                    },
                  }}
                >
                  <Grid
                    container
                    // spacing={applicationType === 'extension' || matchesBigMobile ? 0.5 : 1}
                    spacing={matchesBigMobile ? 0.5 : 1}
                    direction="column"
                  >
                    <Grid size="auto" sx={{ mt: -0.5 }} maxWidth={'100%'}>
                      <Grid container spacing={0} alignItems="center" wrap="nowrap">
                        <Grid size="grow">
                          <Box
                            sx={{
                              width: 80,
                              [theme.breakpoints.down(480)]: {
                                width: 28,
                                ml: 'auto',
                                mr: 1,
                              },
                            }}
                          >
                            <UserAssignee
                              assigneeId={user.NotificationContactUser}
                              assigneeName={user.NotificationContactName}
                              assigneColor={assigneColor}
                            />
                          </Box>
                        </Grid>
                        {interactive && (
                          <Grid size="auto">
                            <UserActions
                              user={user}
                              handleDeleteUsersConfirmation={handleDeleteUsersConfirmation}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                    <Grid size="auto">
                      <Box sx={{ pr: 1 }}>
                        <UserDateAndUnread
                          selected={selected}
                          unreadMessagesNumber={user.UnreadMessagesNumber ?? 0}
                          hoverStyles={hoverStyles}
                          selectedStyles={selectedStyles}
                          unreadStyles={unreadStyles}
                          defaultStyles={defaultStyles}
                          lastMessageDate={user.LastMessageDate ?? ''}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </ListItemText>
        </ListItemButton>
        {!contactsMargin && <Divider sx={{ width: '100%', flex: '0 0 100%', display: 'block' }} flexItem />}
      </React.Fragment>
    );
  },
);

export default SingleChatUser;
